/**
 * [INPUT]: 依赖 react / react-router-dom 的 useNavigate；依赖 ../pipeline 的 scoreImage/reject；
 *   依赖 ./LandingPage ./ResultView ./RejectView ./Footer；依赖 ../report/diagnose-payload；依赖 ../api/diagnose
 * [OUTPUT]: 对外提供 ScoringFlow 组件：落地页→上传→三秒扫描→结果/拒绝状态机，结果页内 AI 诊断 idle/loading/result/error 子状态
 * [POS]: apps/web/components 的评分主流程，由 RR7 框架模式首页路由 (app/routes/_index.tsx) 挂载；
 *   idle/loading 态渲染 LandingPage（即 / 的预渲染快照），result/reject 交互态仅在客户端触发
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { reject, scoreImage, type RejectResult, type ScoreReport } from '../pipeline'
import { LandingPage } from './LandingPage'
import { ResultView } from './ResultView'
import { RejectView } from './RejectView'
import { Footer } from './Footer'
import { buildDiagnosePayload } from '../report/diagnose-payload'
import { DiagnoseError, requestDiagnosis, type DiagnosisState } from '../api/diagnose'
import { trackEvent } from '../analytics'

type Status = 'idle' | 'loading' | 'result' | 'reject'

const SCAN_REVEAL_DELAY_MS = 3000

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/** 评分主流程：落地页→上传→三秒扫描→结果/拒绝状态机，结果页内 AI 诊断 idle/loading/result/error 子状态。 */
export function ScoringFlow() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('idle')
  const [report, setReport] = useState<ScoreReport | null>(null)
  const [rej, setRej] = useState<RejectResult | null>(null)
  const [diagnosis, setDiagnosis] = useState<DiagnosisState>({ status: 'idle' })

  async function handleFile(file: File) {
    setStatus('loading')
    setReport(null)
    setRej(null)
    setDiagnosis({ status: 'idle' })
    try {
      const [res] = await Promise.all([
        scoreImage(file, { gender: 'unknown', genderSource: 'auto' }),
        wait(SCAN_REVEAL_DELAY_MS),
      ])
      if ('total' in res) {
        trackEvent('Score Completed', {
          tier: res.tier,
          score: res.total,
          gender: res.meta.gender,
        })
        setReport(res)
        setStatus('result')
      } else {
        trackEvent('Score Rejected', { code: res.code })
        setRej(res)
        setStatus('reject')
      }
    } catch (err) {
      // 真实错误打到 console 便于诊断（模型加载/CORS/检测异常会被这里兜底成 INVALID_FILE）
      console.error('[ScoringFlow] scoreImage threw:', err)
      trackEvent('Score Rejected', { code: 'INVALID_FILE' })
      setRej(reject('INVALID_FILE'))
      setStatus('reject')
    }
  }

  /** 解锁 AI 专家诊断：仅发脱敏数值，失败保留基础分结果可重试。 */
  async function handleDiagnose() {
    if (!report) return
    setDiagnosis({ status: 'loading' })
    try {
      const payload = buildDiagnosePayload(report)
      const ai = await requestDiagnosis(payload)
      trackEvent('AI Diagnosis Completed', {
        tier: report.tier,
        score: report.total,
        style: ai.style_tag,
      })
      setDiagnosis({ status: 'result', report: ai })
    } catch (e) {
      const code = e instanceof DiagnoseError ? e.code : 'UNKNOWN'
      trackEvent('AI Diagnosis Failed', {
        tier: report.tier,
        score: report.total,
        error_code: code,
      })
      if (e instanceof DiagnoseError) {
        setDiagnosis({ status: 'error', code: e.code, message: e.message })
      } else {
        setDiagnosis({ status: 'error', code: 'UNKNOWN', message: 'Diagnosis failed. Please try again.' })
      }
    }
  }

  function reset() {
    setStatus('idle')
    setReport(null)
    setRej(null)
    setDiagnosis({ status: 'idle' })
  }

  if (status === 'idle' || status === 'loading') {
    return (
      <LandingPage
        onFile={handleFile}
        loading={status === 'loading'}
      />
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col antialiased">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-950/75 sticky top-0 z-50 backdrop-blur-md transition-all duration-300">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => {
              reset()
              navigate('/')
            }}
            className="flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brass to-amber flex items-center justify-center font-bold text-slate-950 shadow-lg shadow-brass/20">
              P
            </div>
            <span className="font-bold text-lg tracking-tight bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
              PSL Analyzer
            </span>
          </button>
          <button
            onClick={() => {
              reset()
              navigate('/')
            }}
            className="text-xs font-semibold text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 bg-slate-900/40 rounded-xl px-4 py-2 transition duration-200"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Main Results Container */}
      <main className="flex-grow flex items-center justify-center px-6 py-12 relative overflow-hidden">
        {/* Glow Effects (LUME Golden Spotlight Glow) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg h-[400px] pointer-events-none opacity-20 bg-amber rounded-full blur-[100px]" />

        <div className="relative z-10 w-full flex justify-center">
          {status === 'result' && report && (
            <ResultView
              report={report}
              onRetry={reset}
              diagnosis={diagnosis}
              onRequestDiagnosis={handleDiagnose}
            />
          )}
          {status === 'reject' && rej && <RejectView result={rej} onRetry={reset} />}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}